import React, { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Clock,
  Calendar,
  Trash2,
  Download,
  Loader2,
  ArrowRight,
  ClipboardList,
} from "lucide-react";
import { Opportunity, UserProfile, ExtractionJob, KeyDate } from "../../types";
import {
  uploadRfpDocument,
  getDocumentDownloadUrl,
  deleteStorageDocument,
} from "../../services/storageService";
import { updateOpportunity, applyExtractedDate } from "../../services/opportunitiesService";
import { startExtraction, subscribeToLatestExtractionJob } from "../../services/extractionService";

interface IntakeTabProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  onSwitchTab?: (tab: string) => void;
  onOpenQualifyModal?: () => void;
}

export const IntakeTab: React.FC<IntakeTabProps> = ({
  opportunity,
  user,
  onSwitchTab,
  onOpenQualifyModal,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [latestJob, setLatestJob] = useState<ExtractionJob | null>(null);
  const [isStartingJob, setIsStartingJob] = useState(false);
  const [appliedDates, setAppliedDates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const unsub = subscribeToLatestExtractionJob(opportunity.id, (job) => {
      setLatestJob(job);
    });
    return () => unsub();
  }, [opportunity.id]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const currentPaths = opportunity.documentPaths || [];
      const newPaths: string[] = [];

      for (const file of Array.from(files)) {
        const res = await uploadRfpDocument(opportunity.id, file);
        newPaths.push(res.fullGsPath);
      }

      await updateOpportunity(opportunity.id, {
        documentPaths: [...currentPaths, ...newPaths],
      });
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err instanceof Error ? err.message : "Failed to upload document.");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveDocument = async (path: string) => {
    if (!window.confirm("Remove this document from the opportunity?")) return;
    try {
      await deleteStorageDocument(path);
    } catch (err) {
      console.warn("Storage deletion warning (ignorable if file was already removed):", err);
    }

    const updated = (opportunity.documentPaths || []).filter((p) => p !== path);
    await updateOpportunity(opportunity.id, {
      documentPaths: updated,
    });
  };

  const handleDownload = async (path: string) => {
    try {
      const url = await getDocumentDownloadUrl(path);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Failed to get download URL:", err);
    }
  };

  const handleRunExtraction = async () => {
    if (!user) return;
    const paths = opportunity.documentPaths || [];
    if (paths.length === 0) {
      alert("Please upload at least one RFP document before running extraction.");
      return;
    }

    setIsStartingJob(true);
    try {
      await startExtraction(opportunity.id, paths, { uid: user.uid, email: user.email });
    } catch (err) {
      console.error("Failed to start extraction job:", err);
      alert(err instanceof Error ? err.message : "Failed to start extraction job.");
    } finally {
      setIsStartingJob(false);
    }
  };

  const handleApplyDate = async (date: KeyDate, idx: number) => {
    try {
      await applyExtractedDate(opportunity.id, date);
      setAppliedDates((prev) => ({ ...prev, [idx]: true }));
    } catch (err) {
      console.error("Failed to apply date:", err);
    }
  };

  const getFileName = (path: string) => {
    const parts = path.split("/");
    const raw = parts[parts.length - 1] || path;
    return raw.replace(/^\d+_/, "");
  };

  const isJobActive =
    latestJob?.status === "queued" || latestJob?.status === "running" || isStartingJob;

  return (
    <div className="space-y-6">
      {/* Top Banner: Qualify Suggestions callout if present */}
      {opportunity.qualifySuggestions && opportunity.qualifySuggestions.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/70 p-4 dark:border-teal-900/50 dark:bg-teal-950/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-[#2ba5b5] shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                  AI Qualify Suggestions Available
                </h4>
                <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                  Gemini analyzed your RFP source documents and prepared{" "}
                  {opportunity.qualifySuggestions.length} criteria recommendations for your Go /
                  No-Go decision.
                </p>
              </div>
            </div>
            {onOpenQualifyModal && (
              <button
                type="button"
                onClick={onOpenQualifyModal}
                className="btn-animate inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#238a97]"
              >
                <ClipboardList className="h-4 w-4" />
                <span>Review Qualify Checklist</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Grid: Upload & Document List / Extraction Engine Card */}
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Left Column: Documents (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                  Source RFP Documents
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Upload PDF or DOCX RFP specifications, addenda, and requirement attachments.
                </p>
              </div>
              <label className="btn-animate inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                <Upload className="h-3.5 w-3.5 text-slate-400" />
                <span>{isUploading ? "Uploading..." : "Upload Document"}</span>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            </div>

            {uploadError && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            {/* Document List */}
            <div className="mt-4 space-y-2">
              {opportunity.documentPaths && opportunity.documentPaths.length > 0 ? (
                opportunity.documentPaths.map((path) => (
                  <div
                    key={path}
                    className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs transition-colors hover:bg-slate-100/70 dark:border-slate-800/60 dark:bg-slate-800/40 dark:hover:bg-slate-800/70"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="rounded-md bg-teal-100/80 p-2 text-[#2ba5b5] dark:bg-teal-950/60">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="truncate">
                        <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                          {getFileName(path)}
                        </p>
                        <p className="text-[11px] text-slate-400 font-mono truncate">{path}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button
                        type="button"
                        onClick={() => handleDownload(path)}
                        title="Download file"
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(path)}
                        title="Remove file"
                        className="rounded-md p-1.5 text-slate-400 hover:bg-rose-100 hover:text-rose-600 dark:hover:bg-rose-950/50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
                  <FileText className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
                  <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                    No documents attached yet
                  </p>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    Upload the client RFP PDF or DOCX to enable AI extraction.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: AI Extraction & Status (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
              <Sparkles className="h-4 w-4 text-[#2ba5b5]" />
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                RFP Requirements Extraction
              </h3>
            </div>

            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Powered by Vertex AI Gemini. Analyzes all attached documents, extracts every question,
              form, format rule, and key date with strict zero-hallucination guardrails.
            </p>

            {/* Current Extraction State */}
            <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/60 p-3.5 dark:border-slate-800 dark:bg-slate-800/40">
              {isJobActive ? (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#2ba5b5]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>
                      {latestJob?.status === "running"
                        ? "Gemini is analyzing RFP documents..."
                        : "Queued for extraction..."}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Model: {latestJob?.model || "gemini-3.8-flash"} (
                    {latestJob?.location || "global"})
                  </p>
                </div>
              ) : latestJob?.status === "complete" ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Extraction Complete</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <div className="rounded-md bg-white p-2 border border-slate-100 dark:bg-slate-900 dark:border-slate-800">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {latestJob.counts?.requirements || 0}
                      </span>{" "}
                      Requirements
                    </div>
                    <div className="rounded-md bg-white p-2 border border-slate-100 dark:bg-slate-900 dark:border-slate-800">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {latestJob.counts?.forms || 0}
                      </span>{" "}
                      Required Forms
                    </div>
                    <div className="rounded-md bg-white p-2 border border-slate-100 dark:bg-slate-900 dark:border-slate-800">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {latestJob.counts?.formatRules || 0}
                      </span>{" "}
                      Format Rules
                    </div>
                    <div className="rounded-md bg-white p-2 border border-slate-100 dark:bg-slate-900 dark:border-slate-800">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {latestJob.counts?.dates || 0}
                      </span>{" "}
                      Key Dates
                    </div>
                  </div>
                  {onSwitchTab && (
                    <button
                      type="button"
                      onClick={() => onSwitchTab("requirements")}
                      className="btn-animate flex w-full items-center justify-center gap-1.5 rounded-lg bg-teal-50 py-2 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:bg-teal-950/40 dark:text-teal-300"
                    >
                      <span>Review Requirements Matrix</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ) : latestJob?.status === "failed" ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-rose-600 dark:text-rose-400">
                    <AlertCircle className="h-4 w-4" />
                    <span>Extraction Failed</span>
                  </div>
                  <p className="text-xs text-rose-700 dark:text-rose-300">
                    {latestJob.error || "An error occurred during extraction."}
                  </p>
                </div>
              ) : (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Ready to extract. Attach documents and run extraction below.
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={handleRunExtraction}
                disabled={
                  isJobActive ||
                  !opportunity.documentPaths ||
                  opportunity.documentPaths.length === 0
                }
                className="btn-animate flex w-full items-center justify-center gap-2 rounded-lg bg-[#2ba5b5] py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-[#238a97] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isJobActive ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Extracting...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    <span>
                      {latestJob?.status === "complete"
                        ? "Re-run Extraction"
                        : "Run RFP Extraction"}
                    </span>
                  </>
                )}
              </button>
              {(!opportunity.documentPaths || opportunity.documentPaths.length === 0) && (
                <p className="mt-1 text-center text-[11px] text-slate-400">
                  Upload an RFP document first to run extraction.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Extracted Key Dates Section */}
      {opportunity.extractedDates && opportunity.extractedDates.length > 0 && (
        <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
            <Calendar className="h-4 w-4 text-[#2ba5b5]" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              Extracted Key RFP Dates
            </h3>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {opportunity.extractedDates.map((dateItem, idx) => (
              <div
                key={`${dateItem.label}-${idx}`}
                className="rounded-lg border border-slate-100 bg-slate-50/70 p-3.5 text-xs dark:border-slate-800 dark:bg-slate-800/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {dateItem.label}
                  </span>
                  {dateItem.sourcePage && (
                    <span className="rounded-md bg-slate-200/70 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      p. {dateItem.sourcePage}
                    </span>
                  )}
                </div>

                <div className="mt-2 flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-mono">{dateItem.date}</span>
                  {dateItem.time && <span className="text-slate-500">({dateItem.time})</span>}
                </div>

                {(dateItem.kind === "proposal_due" || dateItem.kind === "questions_due") && (
                  <button
                    type="button"
                    onClick={() => handleApplyDate(dateItem, idx)}
                    disabled={appliedDates[idx]}
                    className="btn-animate mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-teal-200 bg-white py-1 text-[11px] font-semibold text-[#2ba5b5] hover:bg-teal-50 disabled:bg-emerald-50 disabled:border-emerald-200 disabled:text-emerald-600 dark:border-teal-800 dark:bg-slate-800 dark:text-teal-300 dark:disabled:bg-emerald-950/40 dark:disabled:text-emerald-400"
                  >
                    {appliedDates[idx] ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" />
                        <span>Applied to Tracker</span>
                      </>
                    ) : (
                      <span>Apply to Tracker</span>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
